import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../components/ui/Button";
import { InputSwitch } from "primereact/inputswitch";
import { InputMask } from "primereact/inputmask";
import { InputText } from "primereact/inputtext";

import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import { normaliseMediaBackground, MEDIA_BG_BLACK, MEDIA_BG_WHITE } from "../../../utils/mediaBackground";
import { normaliseRotation, ROTATIONS } from "../../../utils/rotation";
import { readDeviceSettings, storedTime, format12h, endsNextDay } from "../../../utils/deviceSettingsRead";
import "../css/screensHoney.css";   // .pp-scr-seg: the platform's existing two-option segmented control (also used for Live / Screenshot)
import { useEditDeviceSettings } from "../../../queries";
import { useParams } from "react-router-dom";
// import { useConfirmDeviceMutation } from "../../../queries/devices";

// the stored value can be a legacy colour string: the form only ever holds "black" | "white" (same mapping as the backend), so the UI never shows a hex
const normaliseDefaults = (d) => ({ ...d, mediaBackground: normaliseMediaBackground(d.mediaBackground), rotation: normaliseRotation(d.rotation) });

const isValidTime = (t) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(t || "");

// defined at module level: inside the component they were new component types on every render, which remounted every masked time box
const Row = ({ label, testid, children }) => (
    <div className="field flex flex-wrap items-center justify-between gap-2 min-w-0" data-testid={testid}>
        <h5 className="m-0 shrink min-w-0">{label}</h5>
        <div className="flex items-center gap-3 min-w-0">{children}</div>
    </div>
);
const GroupTitle = ({ testid, children }) => <h6 className="m-0 mt-2 text-sm font-semibold text-gray-500" data-testid={testid}>{children}</h6>;
const Toggle = ({ control, name, testid }) => (
    <Controller name={name} control={control} render={({ field }) => (
        <span data-testid={testid}><InputSwitch className="oh-switch" checked={!!field.value} onChange={(e) => field.onChange(e.value)} /></span>
    )} />
);
// a from/to or single time box: one layout for every width (it used to be two copies switched by a breakpoint, 222px wide at xl)
const TimeBox = ({ control, name, disabled, testid, errorPrefix, label }) => (
    <Controller name={name} control={control} rules={{ validate: (v) => isValidTime(v) || "Invalid time" }}
        render={({ field, fieldState }) => (
            <div className="flex flex-col" data-testid={testid}>
                {label && <label className="text-xs text-gray-500 mb-1" data-testid={`${testid}-label`}>{label}</label>}
                <InputMask mask="99:99" placeholder="HH:mm" disabled={disabled} value={field.value ?? ""} onChange={(e) => field.onChange(e.value)}
                    className={`!w-[80px] h-[30px] text-center ${fieldState.invalid ? "p-invalid" : ""}`} />
                {fieldState.error && <small className="p-error mt-1">{errorPrefix}{fieldState.error.message}</small>}
            </div>
        )} />
);

function DeviceSettings() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);
    const { id } = useParams();
    const { selectedDevice } = useSelector((state) => state.device);
    // Replace this with wherever you store the device’s current settings
    const settings = selectedDevice?.device_settings;
    // selectedDevice?.device_settings?.alwaysStart ||
    const defaults = useMemo(
        () => normaliseDefaults({
            alwaysStart: false,
            dailyRestart: false,
            dailyRestartTime: "04:00",
            lockOnTurnOff: false,
            mediaBackground: "black",
            operatingHoursEnabled: false,   // OFF unless stored ON
            operatingStartTime: "09:00",
            operatingSleepTime: "15:05",
            startAtBoot: false,
            // rotation lives in the settings object now; a device saved before that still has it in its legacy column
            rotation: selectedDevice?.rotation ?? 0,
            ...readDeviceSettings(settings),
        }),
        [settings, selectedDevice?.rotation]
    );

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isDirty, isSubmitting },
    } = useForm({
        defaultValues: defaults,
        mode: "onChange",
    });

    const editSettings = useEditDeviceSettings();

    useEffect(() => {
        reset({ ...defaults });
    }, [defaults, reset]);

    // the device's own report (read-only here): true / false / undefined = not reported yet
    const homeRoleHeld = readDeviceSettings(settings).homeRoleHeld;
    const dailyRestart = watch("dailyRestart");
    const operatingHoursEnabled = watch("operatingHoursEnabled");

    const onHide = () => dispatch(toggleDialog("deviceSettings"));

    const onSubmit = async (data) => {
        if (data.dailyRestart && !isValidTime(data.dailyRestartTime)) {
            return alert("Please enter a valid Daily Restart time (HH:mm).");
        }
        if (data.operatingHoursEnabled) {
            if (!isValidTime(data.operatingStartTime) || !isValidTime(data.operatingSleepTime)) {
                return alert("Please enter valid Operating Hours times (HH:mm).");
            }
        }

        // Build payload (include only applicable time fields)
        const payload = {
            alwaysStart: data.alwaysStart,
            dailyRestart: data.dailyRestart,
            dailyRestartTime: data.dailyRestart ? data.dailyRestartTime : defaults.dailyRestartTime,
            lockOnTurnOff: data.lockOnTurnOff,
            mediaBackground: normaliseMediaBackground(data.mediaBackground),   // "black" | "white" only
            operatingHoursEnabled: data.operatingHoursEnabled,
            operatingStartTime: data.operatingHoursEnabled ? data.operatingStartTime : storedTime(settings, "operatingStartTime"),   // OFF: only a time that was really saved, never the placeholder
            operatingSleepTime: data.operatingHoursEnabled ? data.operatingSleepTime : storedTime(settings, "operatingSleepTime"),
            startAtBoot: data.startAtBoot,
            rotation: normaliseRotation(data.rotation),   // 0 | 90 | 180 | 270
        };
        // homeRoleHeld (is PopupPush the Home app) is reported BY the device and shown read-only: it is never part of the payload

        try {
            editSettings.mutate({
                id,
                data: payload,
            });
            // onHide();
        } catch (e) {
            console.error(e);
            alert("Failed to save settings.");
        }
    };

    const footer = (
        <div className="flex items-center justify-end gap-2">
            <Button label={t('buttons.cancel')} className="py-1 p-button-text" onClick={onHide}
                disabled={editSettings.isLoading}
            />
            <Button
                label={t('buttons.save')}
                onClick={handleSubmit(onSubmit)}
                className="py-1"
                loading={editSettings.isLoading}
                // loading={isLoading || isSubmitting}
                disabled={(!isDirty && !isSubmitting) || editSettings.isLoading}
            />
        </div>
    );

    const lang = (i18n && i18n.language) || "en";
    const startVal = watch("operatingStartTime");
    const endVal = watch("operatingSleepTime");
    const fromTxt = format12h(startVal, lang, t('devices.hours_am'), t('devices.hours_pm'));
    const toTxt = format12h(endVal, lang, t('devices.hours_am'), t('devices.hours_pm'));
    const operatingSummary = fromTxt && toTxt
        ? t('devices.hours_summary', { from: fromTxt, to: toTxt }) + (endsNextDay(startVal, endVal) ? " " + t('devices.hours_next_day') : "")
        : "";
    const homeText = homeRoleHeld === true ? t('devices.home_app_on') : homeRoleHeld === false ? t('devices.home_app_off') : t('devices.home_app_unknown');

    return (
        <Dialog
            id='assignDeviceDialog'
            visible={!!openDialog.deviceSettings}
            header={t('devices.group_device_settings')}
            style={{ width: "40%", height: "auto" }}
            breakpoints={{ "1400px": "560px", "960px": "95vw" }}
            modal
            className="p-fluid"
            footer={footer}
            onHide={onHide}
        >
            {/* The same settings, in the same order, as the settings menu on the device itself. The backend copy (device_settings) is the single source of truth. */}
            <div className="card-body flex flex-col gap-3 min-w-0" data-testid="settings-body">

                <GroupTitle testid="settings-group-display">{t('devices.group_section_display')}</GroupTitle>

                {/* rotation */}
                <Row label={t('devices.group_rotation')} testid="row-rotation">
                    <Controller name="rotation" control={control} render={({ field }) => {
                        const current = normaliseRotation(field.value);
                        return (
                            <div className="pp-scr-seg" role="group" dir="ltr" aria-label={t('devices.group_rotation')} data-testid="rotation-seg">
                                {ROTATIONS.map((deg) => (
                                    <button key={deg} type="button" className={current === deg ? 'on' : ''} aria-pressed={current === deg}
                                        data-testid={`rotation-${deg}`} onClick={() => field.onChange(deg)}>{deg}°</button>
                                ))}
                            </div>
                        );
                    }} />
                </Row>

                {/* mediaBackground */}
                <Row label={t('devices.group_media_background')} testid="row-media-bg">
                    <Controller
                        name="mediaBackground"
                        control={control}
                        render={({ field }) => {
                            const current = normaliseMediaBackground(field.value);
                            return (
                                <div className="pp-scr-seg" role="group" aria-label={t('devices.group_media_background')} data-testid="media-bg-seg">
                                    <button type="button" className={current === MEDIA_BG_BLACK ? 'on' : ''} aria-pressed={current === MEDIA_BG_BLACK}
                                        data-testid="media-bg-black" onClick={() => field.onChange(MEDIA_BG_BLACK)}>{t('devices.media_bg_black')}</button>
                                    <button type="button" className={current === MEDIA_BG_WHITE ? 'on' : ''} aria-pressed={current === MEDIA_BG_WHITE}
                                        data-testid="media-bg-white" onClick={() => field.onChange(MEDIA_BG_WHITE)}>{t('devices.media_bg_white')}</button>
                                </div>
                            );
                        }}
                    />
                </Row>

                {/* lock on turn off */}
                <Row label={t('devices.group_lock_on_turn_off')} testid="row-lock"><Toggle control={control} name="lockOnTurnOff" testid="toggle-lock" /></Row>

                <GroupTitle testid="settings-group-operation">{t('devices.group_section_operation')}</GroupTitle>

                {/* start at boot */}
                <Row label={t('devices.group_start_at_boot')} testid="row-start-at-boot"><Toggle control={control} name="startAtBoot" testid="toggle-start-at-boot" /></Row>

                {/* always start */}
                <Row label={t('devices.group_always_start')} testid="row-always-start"><Toggle control={control} name="alwaysStart" testid="toggle-always-start" /></Row>

                {/* dailyRestart + time */}
                <Row label={t('devices.group_daily_restart')} testid="row-daily-restart">
                    <TimeBox control={control} name="dailyRestartTime" disabled={!dailyRestart} testid="daily-restart-time" errorPrefix="" label={t('devices.time_label')} />
                    <Toggle control={control} name="dailyRestart" testid="toggle-daily-restart" />
                </Row>

                {/* operating hours: the toggle, then "Starts" / "Ends" (start on the reading-start side: right in Arabic, left in English) and one sentence saying what it means */}
                <Row label={t('devices.group_operating_hours_enabled')} testid="row-operating-hours">
                    <Toggle control={control} name="operatingHoursEnabled" testid="toggle-operating-hours" />
                </Row>
                <div className="flex flex-col gap-2 min-w-0" data-testid="operating-detail">
                    <div className="flex items-start gap-4 min-w-0">
                        <TimeBox control={control} name="operatingStartTime" disabled={!operatingHoursEnabled} testid="operating-start" errorPrefix="Start: " label={t('devices.hours_starts')} />
                        <TimeBox control={control} name="operatingSleepTime" disabled={!operatingHoursEnabled} testid="operating-end" errorPrefix="End: " label={t('devices.hours_ends')} />
                    </div>
                    {operatingHoursEnabled && operatingSummary && <p className="m-0 text-sm text-gray-600" data-testid="operating-summary">{operatingSummary}</p>}
                </div>

                <GroupTitle testid="settings-group-device">{t('devices.group_section_device')}</GroupTitle>

                {/* Set as Home app: Android only lets the user grant it ON the device, so this is the device's own report, read-only */}
                <Row label={t('devices.group_home_app')} testid="row-home-app">
                    <div className="flex flex-col items-end">
                        <span className="font-medium" data-testid="home-app-state">{homeText}</span>
                        <small className="text-gray-500" data-testid="home-app-hint">{t('devices.home_app_hint')}</small>
                    </div>
                </Row>

            </div >
        </Dialog>
    );
}

export default DeviceSettings;
